import React from 'react';
import { ContentChunkFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { buildTrackingId } from '@/utils/tracking-helper';
import ButtonLink from '@/ui/components/shared/ButtonLink';
import { resolveVideoSource } from '@/utils/video-source';
import { VideoPlayer } from '@/ui/components/shared/video-player/video-player';
import { getVideoAriaLabel, parseVideoSeo } from '@/utils/video-seo';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';

// Slides are much narrower than the viewport: 1.6 of them fit on a phone, 5 on a wide desktop.
const SLIDE_POSTER_SIZES = '(max-width: 640px) 60vw, (max-width: 1024px) 40vw, 20vw';

export interface VideoSliderSlideProps {
  chunk: ContentChunkFragment['chunks'][0];
  index: number;
  activeIndex: number;
  aspectRatioClass: string;
  trackingPrefix?: string;
  /** Asks the slider to make this slide the active one. */
  onActivate?: () => void;
  /** Tells the slider this slide's video played through. */
  onEnded?: () => void;
}

export function VideoSliderSlide({
  chunk,
  index,
  activeIndex,
  aspectRatioClass,
  trackingPrefix,
  onActivate,
  onEnded,
}: Readonly<VideoSliderSlideProps>) {
  const isActive = index === activeIndex;

  const video = extractComponentFromChunk(chunk, 'video', 'VideoContent');
  const videoUrl = extractComponentFromChunk(chunk, 'video-url', 'SingleLineContent');
  const poster = extractComponentFromChunk(chunk, 'poster', 'ImageContent');
  const overlayText = extractComponentFromChunk(chunk, 'overlay-text', 'RichTextContent');
  const cta = extractComponentFromChunk(chunk, 'cta', 'PieceContent');
  const videoSeo = extractComponentFromChunk(chunk, 'video-seo', 'PieceContent');

  const source = resolveVideoSource(video, videoUrl?.text);
  if (!source) {
    return null;
  }

  const seo = parseVideoSeo(videoSeo);
  const ariaLabel = getVideoAriaLabel(seo, overlayText?.plainText?.[0]);
  const slideTrackingId = buildTrackingId(trackingPrefix, `slide-${index}`);

  // A click on a neighbouring slide brings it into position first. The editorial overlay keeps
  // working as it is - its CTA is a link or a button, and both must stay reachable on an inactive
  // slide; the activated slide starts by itself, so the same click must not also toggle it back
  // to paused.
  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    const isOverlayContent = Boolean(target?.closest('[data-slide-overlay]'));
    if (isActive || !onActivate || isOverlayContent) {
      return;
    }

    onActivate();
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div onClickCapture={handleClickCapture}>
      <VideoPlayer
        source={source}
        poster={poster?.images?.[0] ?? source.thumbnail}
        ariaLabel={ariaLabel}
        mode="in-view"
        aspectRatioClass={aspectRatioClass}
        isActive={isActive}
        posterSizes={SLIDE_POSTER_SIZES}
        className="rounded-xl"
        controlsAnchor="top-left"
        trackingId={slideTrackingId}
        onEnded={onEnded}
      >
        {/* The Column Slider V2 card gradient, value for value: 60 % of `neutral-100` rising over
            the bottom half. Written as an explicit box so it does not depend on the order Tailwind
            emits `inset-0` and `top-1/2` in. */}
        <div
          data-testid="video-slider-slide-gradient"
          className="absolute inset-x-0 bottom-0 top-1/2 bg-gradient-to-t from-neutral-100/60 to-transparent"
        />
        {/* The text block of the Column Slider V2 cards: left-aligned at `bottom-5` with the same
            padding, the same title size and the same `mb-2` above a content-width CTA, so a slider
            and a card sitting next to each other read as one family. `inset-x-0` is the one
            deliberate difference - V2 lets its block shrink-wrap, which on a 9:16 slide would give
            an unpredictable text column, so the overlay spans the slide width instead. */}
        <div
          data-testid="video-slider-slide-overlay"
          data-slide-overlay
          className="absolute inset-x-0 bottom-5 px-4 text-white lg:px-6"
        >
          <RichTextRenderer blocks={overlayText} className="mb-2 text-2xl lg:text-3xl" />
          <ButtonLink ctaComponent={cta} className="w-fit" trackingPrefix={slideTrackingId} />
        </div>
      </VideoPlayer>
    </div>
  );
}
